import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, CheckCircle2, XCircle } from "lucide-react";

// Lightweight QR reader using BarcodeDetector when available, else manual entry.
export default function CheckIn() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [last, setLast] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const activeRef = useRef(false);

  useEffect(() => { api.get("/admin/events").then((r) => setEvents(r.data.filter(e => e.status === "published"))); }, []);

  const submit = async (ticket_id) => {
    if (!eventId) return toast.error("Select an event first");
    if (!ticket_id) return;
    try {
      const r = await api.post("/checkin", { ticket_id, event_id: eventId });
      setLast({ ok: true, ...r.data });
      toast.success(`Valid: ${r.data.attendee?.name}`);
    } catch (e) {
      setLast({ ok: false, msg: e.response?.data?.detail || "Invalid" });
      toast.error(e.response?.data?.detail || "Invalid ticket");
    }
  };

  const start = async () => {
    if (!("BarcodeDetector" in window)) {
      return toast.info("QR camera not supported here. Use manual entry.");
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setScanning(true);
      activeRef.current = true;
      const det = new window.BarcodeDetector({ formats: ["qr_code"] });
      const loop = async () => {
        if (!activeRef.current) return;
        try {
          const codes = await det.detect(videoRef.current);
          if (codes[0]?.rawValue) {
            submit(codes[0].rawValue);
            stop();
            return;
          }
        } catch (_e) { /* ignore frame errors */ }
        requestAnimationFrame(loop);
      };
      requestAnimationFrame(loop);
    } catch (e) { toast.error("Camera access denied"); }
  };
  const stop = () => {
    activeRef.current = false;
    setScanning(false);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
  };

  return (
    <div className="max-w-2xl">
      <div className="eyebrow">Door</div>
      <h1 className="font-display text-3xl font-bold mt-2">Check-in</h1>
      <div className="mt-6 cgc-card p-5 space-y-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-stone-500 mb-1">Event</div>
          <Select value={eventId} onValueChange={setEventId}>
            <SelectTrigger data-testid="checkin-event-select"><SelectValue placeholder="Choose an event" /></SelectTrigger>
            <SelectContent>{events.map(e => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="border border-dashed border-border rounded-lg p-4">
          <div className="aspect-square w-full bg-black/90 rounded-md overflow-hidden relative">
            <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
            {!scanning && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-xs">Camera off</div>
            )}
            <div className="absolute inset-6 border-2 border-white/60 rounded-lg pointer-events-none" />
          </div>
          <div className="mt-3 flex gap-2">
            {!scanning
              ? <Button data-testid="checkin-scan-start" className="btn-brand" onClick={start}><Camera size={14} className="mr-1.5"/>Start camera</Button>
              : <Button data-testid="checkin-scan-stop" variant="outline" onClick={stop}>Stop</Button>}
          </div>
        </div>

        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-stone-500 mb-1">Manual lookup</div>
          <div className="flex gap-2">
            <Input data-testid="checkin-manual-input" placeholder="Paste ticket ID" value={manual} onChange={(e) => setManual(e.target.value)} />
            <Button data-testid="checkin-manual-submit" onClick={() => submit(manual)}>Verify</Button>
          </div>
        </div>

        {last && (
          <div data-testid="checkin-result" className={`p-4 rounded-md flex items-center gap-3 ${last.ok ? "bg-green-100 text-green-900" : "bg-red-100 text-red-900"}`}>
            {last.ok ? <CheckCircle2 /> : <XCircle />}
            <div>
              <div className="font-semibold">{last.ok ? "Valid ticket" : "Invalid"}</div>
              <div className="text-sm">{last.ok ? `${last.attendee?.name} · ${last.booking_reference}` : last.msg}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
